/**
 * Array utilities — sorting, deduplication, removal.
 *
 * Canonical implementations consolidated from:
 * - packages/ssm-web5/src/utils/array.js
 * - packages/hoi-poi-ui/src/utils/arrays.js
 * - apps/ssm-web4/src/app/utils/arrays/index.js
 * - apps/ssm-admin/src/utils/array.js
 */

/**
 * Sort an array of objects by a given key. Values are compared as numbers when
 * numeric, otherwise as lower-cased strings.
 *
 * @param {Array<Object>} arr
 * @param {string} key
 * @returns {Array<Object>} The sorted array (mutates in place)
 */
export const sort = (arr, key) => {
    if (!arr || arr.length === 0) return arr;
    return arr.sort((a, b) => {
        const nameA = isNaN(a[key]) ? a[key].toLowerCase() : Number(a[key]);
        const nameB = isNaN(b[key]) ? b[key].toLowerCase() : Number(b[key]);
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
    });
};

/**
 * Remove an element from an array in place (by reference or by attribute match).
 *
 * @param {Array} array
 * @param {*} element — the element (or value of `attr`) to remove
 * @param {string} [attr] — if provided, match `item[attr] === element`
 */
export function remove(array, element, attr) {
    let index = -1;
    if (attr) {
        index = array.findIndex((item) => item[attr] === element);
    } else {
        index = array.indexOf(element);
    }
    if (index > -1) {
        array.splice(index, 1);
    }
}

/**
 * Sort an array of objects by a property, with an optional transform function.
 *
 * @param {Array<Object>} arr
 * @param {string} prop
 * @param {Function} [t] — optional transform applied to the property value before comparison
 * @returns {Array<Object>}
 */
export function sortByProp(arr, prop, t) {
    if (!arr || !prop) return [];
    return arr.sort((a, b) => {
        const aProp = t ? t(a[prop]) : a[prop];
        const bProp = t ? t(b[prop]) : b[prop];
        if (aProp < bProp) return -1;
        if (aProp > bProp) return 1;
        return 0;
    });
}

/**
 * Convert an array of objects into an object keyed by a given property.
 *
 * @param {Array<Object>} array
 * @param {string} key
 * @returns {Object}
 */
export const arrayToObject = (array, key) => {
    return array.reduce((obj, item) => {
        return {
            ...obj,
            [item[key]]: item,
        };
    }, {});
};

/**
 * Return unique elements from an array based on a key, preserving order.
 *
 * @param {Array<Object>} arr
 * @param {string} key
 * @returns {Array<Object>}
 */
export function getUniqueKeys(arr, key) {
    return arr
        .map((e) => e[key])
        .map((e, i, final) => final.indexOf(e) === i && i)
        .filter((e) => arr[e])
        .map((e) => arr[e]);
}

/**
 * Shallow equality check for two arrays (same length, same values by `===`).
 *
 * Exported as `arraysIsEqual` to avoid name collision with the deep `isEqual`
 * in objects.js.
 *
 * @param {Array} a
 * @param {Array} b
 * @returns {boolean}
 */
export const arraysIsEqual = (a, b) => {
    return (
        Array.isArray(a) &&
        Array.isArray(b) &&
        a.length === b.length &&
        a.every((val, index) => val === b[index])
    );
};
